/**
 * ContactSidebar - Barra lateral de informações do contato
 * Exibe detalhes do contato, mídias, etiquetas e histórico de conversas
 * 
 * Versão: 2.1.0
 * - Adicionado ícone para abrir modal de cliente vinculado (CRM)
 * - Adicionado suporte a Tags de Negócio (CRM) baseado no perfil do usuário
 * - Admin/Supervisor: vê Tags do Contato + Tags de Negócio
 * - Vendedor: vê apenas Tags de Negócio
 * - Agente: vê apenas Tags do Contato
 * 
 * RELEASE: 2026-01-10
 */
import { useState, useEffect } from 'react';
import type { Database } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AvatarWithLightbox } from '@/components/ui/avatar-with-lightbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { X, Phone, Mail, MessageSquare, Calendar, User, Tag, UserPlus, Loader2, Smartphone, Check, Copy, Clock, AlertCircle, Plus, Minus, Inbox, History, Tags, Pencil, Settings, Bot, BotOff, ArrowLeft, Briefcase, ChevronRight, Instagram, Send, Globe, MessagesSquare, ExternalLink, RefreshCw, ListChecks, Activity, BarChart3, Target, Layers, Paperclip, Sparkles } from 'lucide-react';
import { SectionCard, SectionRow } from './ContactSidebarSectionCard';
// v: subabas por tipo de tarefa (Normal | Retenção | Jurídico)
import { TasksRelatedTabs } from '@/components/tasks/related/TasksRelatedTabs';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { ConversationHistoryModal } from './ConversationHistoryModal';
import { Contact, Conversation } from '@/pages/tenant/Chat';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getStorageUrl } from '@/lib/storage';
import { useProviderUtils } from '@/hooks/useProviderUtils';
import { toast } from 'sonner';
import { ConversationMediaSection } from './ConversationMediaSection';
import { ManualRoutingSection } from './ManualRoutingSection';
import { useSessionWindow } from '@/hooks/useSessionWindow';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLabels } from '@/hooks/useLabels';
import { LabelBadgeList, AssignLabelsDialog } from '@/components/tenant/labels';
import { LabelManager } from '@/components/tenant/labels/LabelManager';
import { isLabelsSupported } from '@/lib/labelsConfig';
import { AssignTagsDialog } from '@/components/tenant/contacts/AssignTagsDialog';
import { useContactTags } from '@/hooks/useContactTags';
import { useCurrentUserSeller } from '@/hooks/useCurrentUserSeller';
import { useContactDealTags } from '@/hooks/useContactDealTags';
import { useContactSidebarDetails } from '@/hooks/useContactSidebarDetails';
import { DealTagBadge } from '@/components/crm/deals/tags/DealTagBadge';
import { ContactDealsSection } from './ContactDealsSection';
import { ContactActivitiesSection } from './ContactActivitiesSection';
import { StartWhatsAppSection } from './StartWhatsAppSection';
import { InlineCategoryPicker, InlinePhasePicker } from './InlineKanbanPickers';
import { createLogger } from '@/lib/logger';

// Logger para operações de sincronização de contato (logs condicionais por ambiente)
const syncLogger = createLogger('ContactSidebar');

interface ContactSidebarProps {
  contact: Contact;
  conversation: Conversation;
  onClose: () => void;
  /** Timestamp da última mensagem inbound (para cálculo janela 24h) */
  lastInboundAt?: string | null;
  /** Callback para navegar para outra conversa do histórico */
  onNavigateToConversation?: (conversationId: string, inboxId: string) => void;
  /** Callback para atualizar dados da conversa (ex: após salvar tags) */
  onRefreshConversation?: () => void;
  /** Callback para pausar/reativar bot na conversa (Human Takeover) */
  onToggleBotPause?: () => Promise<void>;
  /** Indica se está processando toggle de pausa do bot */
  isTogglingBotPause?: boolean;
}
export function ContactSidebar({
  contact,
  conversation,
  onClose,
  lastInboundAt,
  onNavigateToConversation,
  onRefreshConversation,
  onToggleBotPause,
  isTogglingBotPause
}: ContactSidebarProps) {
  // Estado para loading do botão de salvar na agenda
  const [isAddingContact, setIsAddingContact] = useState(false);
  // Estado para controlar abertura do acordeão de histórico
  const [historyOpen, setHistoryOpen] = useState(false);
  // Estado para controlar abertura da seção de Tarefas (lazy render)
  const [tasksOpen, setTasksOpen] = useState(false);
  // TEMPORARIAMENTE DESABILITADO - Aguardando correção integração WAHA
  // const [showLabelsDialog, setShowLabelsDialog] = useState(false);
  // const [showLabelManagerDialog, setShowLabelManagerDialog] = useState(false);
  // Estado para dialog de atribuição de tags ao contato
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  // Estado para modal de histórico de movimentações
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // Categoria do Kanban selecionada localmente (preview) — só persiste quando o usuário escolhe uma fase
  const [pendingCategoryId, setPendingCategoryId] = useState<string | null>(null);
  // Reseta o preview ao trocar de conversa para evitar arrastar estado entre contatos
  useEffect(() => {
    setPendingCategoryId(null);
  }, [conversation?.id]);
  
  // Estado para sincronização manual do contato via provider WAHA
  const [isSyncingContact, setIsSyncingContact] = useState(false);
  
  // Estados para edição inline do nome do contato
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(contact.name || '');
  const [isSavingName, setIsSavingName] = useState(false);
  
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  const {
    addToPhoneContacts
  } = useProviderUtils();
  
  // Verificar perfil do usuário para exibição condicional de tags
  const { seller, hasFullAccess } = useCurrentUserSeller();
  const crmEnabled = currentTenant?.crm_enabled ?? false;
  
  // Lógica de exibição de tags:
  // - Admin/Supervisor (hasFullAccess): ambos
  // - Vendedor (seller vinculado): apenas tags de negócio
  // - Agente comum: apenas tags do contato
  const isSeller = !!seller;
  const showContactTags = hasFullAccess || !isSeller;
  const showDealTags = crmEnabled && (hasFullAccess || isSeller);
  
  // Query para tags de negócio (lazy loading - só se necessário)
  const { data: dealTagsData, isLoading: loadingDealTags } = useContactDealTags(
    contact.id,
    { enabled: showDealTags }
  );
  
  // ====================================================================
  // RPC unificada do sidebar — substitui useContactSidebarCounts e
  // fornece dados consolidados (contact, contact_tags, conversation, counts).
  //
  // IMPORTANTE: Para campos atualizados em tempo real (last_inbound_at,
  // bot_paused), as PROPS continuam vencendo — a RPC só fornece snapshot
  // inicial. Isso preserva a reatividade do Realtime para a janela 24h.
  // ====================================================================
  const { data: sidebarDetails } = useContactSidebarDetails({
    contactId: contact.id,
    inboxId: conversation.inbox_id,
    conversationId: conversation.id,
    enabled: !!contact.id && !!conversation.inbox_id,
  });

  // Counts vindos da RPC unificada (mantém alias `sidebarCounts` p/ compatibilidade)
  const sidebarCounts = sidebarDetails?.counts;

  // Tags do contato vindas da RPC (lista completa com id/name/color)
  const contactTagsFromRpc = sidebarDetails?.contact_tags ?? [];

  // Chave única da RPC do sidebar para manter contador, preview e dialogs sincronizados.
  const contactSidebarDetailsQueryKey = [
    'contact-sidebar-details',
    contact.id,
    conversation.inbox_id,
    conversation.id,
  ] as const;

  // Merge defensivo: dados da conversa preferem RPC, mas campos reativos
  // (last_inbound_at, bot_paused) usam props para preservar Realtime
  const mergedConversation = {
    ...conversation,
    ...(sidebarDetails?.conversation ?? {}),
    // Campos reativos sempre vêm das props (atualizados via Realtime)
    last_inbound_at: lastInboundAt ?? conversation.last_inbound_at,
    bot_paused: conversation.bot_paused,
    bot_paused_at: conversation.bot_paused_at,
    // inbox_id obrigatório para queries antigas
    inbox_id: conversation.inbox_id,
  };

  // Estado para modal de cliente vinculado
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  // Query para buscar cliente vinculado ao contato (CRM) - via RPC otimizada
  const { data: linkedCustomer } = useQuery({
    queryKey: ['contact-linked-customer', contact.id, currentTenant?.id],
    queryFn: async () => {
      if (!contact.id || !currentTenant?.id) return null;

      const { data, error } = await supabase.rpc('get_contact_linked_customer', {
        p_contact_id: contact.id,
      });

      if (error) throw error;
      // RPC retorna array, pegamos o primeiro (equivale ao .maybeSingle()).
      // v2.0.0 da RPC: a linha sempre existe — quando não há cliente vinculado,
      // `id` vem nulo e a linha traz apenas os dados do contato (contact_*).
      return data && data.length > 0 ? data[0] : null;
    },
    enabled: !!contact.id && !!currentTenant?.id && crmEnabled,
  });

  // Hook para calcular estado da janela 24h
  // Usa channel_type da conversa mesclada (RPC) e lastInboundAt das props (Realtime)
  const sessionWindow = useSessionWindow(
    mergedConversation.inbox?.channel_type,
    lastInboundAt
  );
  
  // Query com lazy loading - só executa quando acordeão está aberto - via RPC otimizada
  const { data: conversationHistory, isLoading: loadingHistory } = useQuery({
    queryKey: ['contact-conversation-history', contact.id, conversation.inbox_id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contact_conversation_history', {
        p_contact_id: contact.id,
        p_exclude_inbox_id: conversation.inbox_id,
      });
      
      if (error) throw error;
      return data;
    },
    enabled: historyOpen && !!contact.id, // Lazy loading - só carrega quando abre
  });
  
  // TEMPORARIAMENTE DESABILITADO - Aguardando correção integração WAHA
  // Hook para gerenciamento de etiquetas - só ativa se provider suporta
  // const labelsSupported = isLabelsSupported(conversation?.inbox?.provider_slug);
  
  // const { 
  //   labels: availableLabels, 
  //   useConversationLabels, 
  //   assignLabels, 
  //   assigningLabels 
  // } = useLabels({ 
  //   inboxId: conversation?.inbox_id || null, 
  //   enabled: !!conversation?.inbox_id && labelsSupported 
  // });
  
  // Busca etiquetas atribuídas à conversa atual (só se provider suporta)
  // const { data: conversationLabels = [] } = useConversationLabels(
  //   labelsSupported ? conversation?.id : null
  // );

  // Tags disponíveis do tenant via hook centralizado (RPC get_tenant_contact_tags)
  const { data: availableTags = [] } = useContactTags(currentTenant?.id);


  // Sincroniza estado de edição quando contato muda
  useEffect(() => {
    setEditedName(contact.name || '');
    setIsEditingName(false);
  }, [contact.id, contact.name]);

  // Obtém iniciais do nome do contato
  const getInitials = (name: string | null) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  /**
   * Salva o nome editado do contato no banco
   */
  const handleSaveName = async () => {
    const trimmedName = editedName.trim();
    
    if (!trimmedName) {
      toast.error('O nome não pode estar vazio');
      return;
    }
    
    if (trimmedName === contact.name) {
      setIsEditingName(false);
      return;
    }
    
    setIsSavingName(true);
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ name: trimmedName })
        .eq('id', contact.id);
      
      if (error) throw error;
      
      toast.success('Nome atualizado com sucesso');
      setIsEditingName(false);
      
      // Invalida queries para atualizar dados
      queryClient.invalidateQueries({ queryKey: ['contact-full-data', contact.id] });
      queryClient.invalidateQueries({ queryKey: ['contact-linked-customer', contact.id] });
      onRefreshConversation?.();
    } catch (error) {
      console.error('[ContactSidebar] Erro ao atualizar nome:', error);
      toast.error('Erro ao atualizar nome');
    } finally {
      setIsSavingName(false);
    }
  };

  /**
   * Cancela a edição do nome e restaura valor original
   */
  const handleCancelEditName = () => {
    setEditedName(contact.name || '');
    setIsEditingName(false);
  };

  /**
   * Trata teclas no input de edição do nome
   */
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  /**
   * Formata número de telefone para exibição
   * Ex: 5562993252289 -> +55 62 99325-2289
   */
  const formatPhoneDisplay = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');

    // Formato brasileiro com 13 dígitos: +55 XX XXXXX-XXXX
    if (digits.length === 13 && digits.startsWith('55')) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }

    // Formato brasileiro com 12 dígitos (fixo): +55 XX XXXX-XXXX
    if (digits.length === 12 && digits.startsWith('55')) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
    }

    // Formato brasileiro sem código de país (11 dígitos celular)
    if (digits.length === 11) {
      return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    // Formato brasileiro sem código de país (10 dígitos fixo)
    if (digits.length === 10) {
      return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    // Se já tem +, mantém como está
    if (phone.startsWith('+')) return phone;

    // Fallback: retorna original
    return phone;
  };

  /**
   * Copia o número de telefone para a área de transferência
   */
  const handleCopyPhone = async () => {
    if (!contact.phone) return;
    try {
      const digits = contact.phone.replace(/\D/g, '');
      await navigator.clipboard.writeText(digits);
      toast.success('Telefone copiado!');
    } catch (error) {
      toast.error('Erro ao copiar telefone');
    }
  };

  /**
   * Handler para adicionar contato na agenda do WhatsApp
   * Separa o nome em firstName e lastName e chama a API
   */
  const handleAddToPhoneContacts = async () => {
    if (!contact.phone) {
      toast.error('Contato precisa ter telefone');
      return;
    }
    setIsAddingContact(true);
    try {
      // Separa nome em firstName e lastName
      const nameParts = (contact.name || 'Contato').split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || undefined;
      const result = await addToPhoneContacts(conversation.inbox_id, [{
        firstName,
        lastName,
        phone: contact.phone.replace(/\D/g, '')
      }]);
      
      if (result.success) {
        // =========================================================================
        // NOTA: A Edge Function já persiste is_saved_to_agenda=true no banco
        // Este update é redundância para garantir consistência mesmo se falhar lá
        // RELEASE: 2026-01-29 - Melhorado feedback de erro
        // =========================================================================
        const { error: updateError } = await supabase
          .from('contacts')
          .update({ 
            is_saved_to_agenda: true,
            saved_to_agenda_at: new Date().toISOString()
          })
          .eq('id', contact.id);
        
        // Invalida queries para refletir mudança imediatamente
        queryClient.invalidateQueries({ queryKey: ['contact-full-data', contact.id] });
        onRefreshConversation?.();
        
        if (updateError) {
          // Avisa mas não impede sucesso (server-side já salvou)
          console.warn('[ContactSidebar] Falha no update local (server-side já persistiu):', updateError);
          toast.success('Contato salvo na agenda do WhatsApp!');
        } else {
          toast.success('Contato salvo na agenda do WhatsApp!');
        }
      } else {
        toast.error(result.error || 'Erro ao salvar contato');
      }
    } catch (error) {
      console.error('[ContactSidebar] Erro ao salvar contato na agenda:', error);
      toast.error('Erro ao salvar contato na agenda');
    } finally {
      setIsAddingContact(false);
    }
  };

  /**
   * Sincroniza dados do contato individual via provider WAHA
   * 
   * Busca nome atualizado (pushName) e foto de perfil do WhatsApp,
   * atualizando a tabela contacts e invalidando queries relevantes.
   * Disponível apenas para inboxes com provider_slug === 'waha'.
   */
  const handleSyncContact = async () => {
    // Validações: precisa ser provider waha e ter telefone
    const providerSlug = conversation.inbox?.provider_slug;
    if (providerSlug !== 'waha' || !contact.phone) return;

    setIsSyncingContact(true);
    const inboxId = conversation.inbox_id;
    const phone = contact.phone.replace(/\D/g, '');

    try {
      // Tipo seguro do Supabase para update de contatos
      const updatePayload: Database['public']['Tables']['contacts']['Update'] = {};

      // 1. Buscar informações atualizadas do contato (nome/about)
      syncLogger.debug('Sincronizando contato individual', { contactId: contact.id, phone });
      const { data: contactInfo, error: infoError } = await supabase.functions.invoke('provider-proxy', {
        body: { inbox_id: inboxId, action: 'get_contact_info', phone }
      });

      if (!infoError && contactInfo?.data?.name) {
        const newName = contactInfo.data.name;
        // Só atualiza se nome mudou e nome atual parece ser apenas número (fallback)
        const currentIsPhoneOnly = /^\+?[\d\s\-\(\)]+$/.test(contact.name || '');
        if (newName !== contact.name || currentIsPhoneOnly) {
          updatePayload.name = newName;
          syncLogger.info('Nome atualizado via sync', { oldName: contact.name, newName });
        }
      }

      // 2. Baixar e salvar avatar atualizado no Storage (isolado em try-catch próprio)
      try {
        const { data: avatarResult, error: avatarError } = await supabase.functions.invoke('provider-proxy', {
          body: { inbox_id: inboxId, action: 'download_avatar', phone, contact_id: contact.id }
        });

        if (!avatarError && avatarResult?.data?.storage_path) {
          updatePayload.avatar_url = avatarResult.data.storage_path;
          syncLogger.info('Avatar atualizado via sync', { storagePath: avatarResult.data.storage_path });
        }
      } catch (avatarErr) {
        // Foto não encontrada é situação normal (contato sem foto de perfil)
        syncLogger.debug('Avatar não disponível para este contato', { phone });
      }

      // 3. Persistir alterações na tabela contacts (se houve mudança)
      if (Object.keys(updatePayload).length > 0) {
        const { error: updateError } = await supabase
          .from('contacts')
          .update(updatePayload)
          .eq('id', contact.id);

        if (updateError) throw updateError;

        // Invalida queries para refletir mudanças imediatamente
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({ queryKey: ['contact-full-data', contact.id] });
        onRefreshConversation?.();

        toast.success('Contato sincronizado com sucesso!');
      } else {
        toast.info('Contato já está atualizado');
      }
    } catch (error) {
      syncLogger.error('Erro ao sincronizar contato', error);
      toast.error('Erro ao sincronizar contato');
    } finally {
      setIsSyncingContact(false);
    }
  };

  return <div className="w-full md:w-80 h-full bg-card flex flex-col rounded-lg shadow-sm border overflow-hidden">
      {/* Header - Responsivo para mobile */}
      <div className="h-16 px-4 flex items-center justify-between rounded-t-lg border-b md:border-b-0">
        <div className="flex items-center gap-2">
          {/* Botão voltar - visível apenas em mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-8 w-8"
            onClick={onClose}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h3 className="font-semibold">Informações do Contato</h3>
          
          {/* Ícone para abrir modal de cliente (só aparece se CRM habilitado e cliente vinculado) */}
          {/* v2.0.0 da RPC: só exibir quando há cliente de fato (id não nulo) */}
          {linkedCustomer?.id && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsCustomerModalOpen(true)}
                >
                  <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Ver cliente</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Botão X - visível apenas em desktop */}
        <Button variant="ghost" size="icon" onClick={onClose} className="hidden md:inline-flex">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3 bg-muted/30 min-h-full">
          {/* Bloco de identidade do contato — sem wrapper de card (layout original pré-refatoração) */}
          {/* Contact Profile com indicador de status do bot */}
          <div className="flex flex-col items-center text-center">
            {/* Avatar com badge de status do bot */}
            <div className="relative mb-3">
              {/* Avatar com lightbox para visualização ampliada */}
              <AvatarWithLightbox
                src={contact.avatar_url}
                name={contact.name}
                email={contact.phone}
                className="h-20 w-20 text-2xl"
                size="2xl"
               />
              
              {/* Botão de sincronização manual - visível apenas para provider WAHA */}
              {conversation.inbox?.provider_slug === 'waha' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleSyncContact}
                      disabled={isSyncingContact}
                      className={cn(
                        "absolute -top-1 -right-1 p-1 rounded-full border-2 border-background shadow-sm bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors",
                        isSyncingContact && "opacity-50 cursor-not-allowed"
                      )}
                      title="Sincronizar contato"
                    >
                      <RefreshCw className={cn("h-3 w-3", isSyncingContact && "animate-spin")} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">Sincronizar nome e foto</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Badge indicador de status do bot - canto inferior direito do avatar */}
              <div 
                className={cn(
                  "absolute -bottom-1 -right-1 p-1.5 rounded-full border-2 border-background shadow-sm transition-colors",
                  conversation.bot_paused 
                    ? "bg-destructive/10 text-destructive" 
                    : "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                )}
                title={conversation.bot_paused ? "Bot pausado" : "Bot ativo"}
              >
                {conversation.bot_paused ? (
                  <BotOff className="h-3.5 w-3.5" />
                ) : (
                  <Bot className="h-3.5 w-3.5" />
                )}
              </div>
            </div>
            
            {/* Nome do contato com edição inline */}
            <div className="flex items-center justify-center gap-1 group/name">
              {isEditingName ? (
                // Modo edição
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    autoFocus
                    className="text-lg font-semibold text-center bg-transparent border-b-2 border-primary focus:outline-none px-1 min-w-[100px] max-w-[200px]"
                    disabled={isSavingName}
                  />
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleSaveName}
                      disabled={isSavingName}
                    >
                      {isSavingName ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleCancelEditName}
                      disabled={isSavingName}
                    >
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              ) : (
                // Modo visualização
                <>
                  <h4 className="font-semibold text-lg">
                    {contact.name || 'Sem nome'}
                  </h4>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover/name:opacity-100 transition-opacity"
                        onClick={() => setIsEditingName(true)}
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="text-xs">Editar nome</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
            
            {/* Identificador do contato - Telefone E/OU Instagram */}
            {(() => {
              // Busca username primeiro em channel_ids, fallback para metadata (compatibilidade)
              const channelIds = contact.channel_ids as { instagram_username?: string; instagram_igsid?: string } | null;
              const instagramUsername = channelIds?.instagram_username || 
                (contact.metadata as { username?: string })?.username;
              
              return (
                <div className="flex flex-col gap-1">
                  {/* Exibe telefone se existir */}
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Smartphone className="text-muted-foreground/70 w-[14px] h-[19px]" />
                      <span>{formatPhoneDisplay(contact.phone)}</span>
                      <button onClick={handleCopyPhone} className="p-1 rounded hover:bg-muted transition-colors" title="Copiar telefone">
                        <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  )}
                  
                  {/* Exibe Instagram se existir (mesmo em conversas WhatsApp) */}
                  {instagramUsername && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Instagram className="text-pink-500 w-[14px] h-[19px]" />
                      <span className="font-medium">@{instagramUsername}</span>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(instagramUsername);
                          toast.success('Username copiado!');
                        }} 
                        className="p-1 rounded hover:bg-muted transition-colors" 
                        title="Copiar username"
                      >
                        <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                      <a 
                        href={`https://instagram.com/${instagramUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 rounded hover:bg-muted transition-colors"
                        title="Abrir perfil no Instagram"
                      >
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </a>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Container para botões de controle - Pausar Bot e Histórico */}
            <div className="flex items-center justify-center gap-2 mt-2">
              {/* Botão de controle do bot - elegante e compacto */}
              {onToggleBotPause && (
                <Button
                  variant={conversation.bot_paused ? "outline" : "ghost"}
                  size="sm"
                  onClick={onToggleBotPause}
                  disabled={isTogglingBotPause}
                  className={cn(
                    "h-6 gap-1.5 px-2.5 py-0.5 text-xs font-medium transition-all",
                    conversation.bot_paused 
                      ? "border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {isTogglingBotPause ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : conversation.bot_paused ? (
                    <>
                      <Bot className="h-3.5 w-3.5" />
                      Reativar Bot
                    </>
                  ) : (
                    <>
                      <BotOff className="h-3.5 w-3.5" />
                      Pausar Bot
                    </>
                  )}
                </Button>
              )}
              
              {/* Botão de Histórico de Movimentações */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistoryModal(true)}
                className="h-6 gap-1.5 px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <History className="h-3.5 w-3.5" />
                Histórico
              </Button>
          </div>
          {/* /flex flex-col items-center text-center */}
          </div>

          

          
          {/* Modal de Histórico de Movimentações */}
          <ConversationHistoryModal
            open={showHistoryModal}
            onOpenChange={setShowHistoryModal}
            conversationId={conversation.id}
          />

          {/* Seção Iniciar WhatsApp - exibida para contatos sem telefone válido ou de canais não-WhatsApp */}
          {(() => {
            // Verifica se canal atual não é WhatsApp
            const isNonWhatsAppChannel = ['instagram', 'webchat', 'facebook', 'email'].includes(
              conversation.inbox?.channel_type || ''
            );
            
            // Após migration, campo phone só contém telefones reais
            // Verifica se contato tem telefone válido para WhatsApp
            const hasValidWhatsAppPhone = (() => {
              if (!contact.phone) return false;
              const digits = contact.phone.replace(/\D/g, '');
              // Deve ter pelo menos 10 dígitos para ser um telefone válido
              return digits.length >= 10 && digits.length <= 15;
            })();
            
            // Exibe seção se: canal não é WhatsApp OU não tem telefone válido
            const shouldShowSection = isNonWhatsAppChannel || !hasValidWhatsAppPhone;
            
            if (!shouldShowSection || !currentTenant?.id) return null;
            
            return (
              <StartWhatsAppSection
                contact={contact}
                conversation={conversation}
                tenantId={currentTenant.id}
                onPhoneSaved={() => {
                  queryClient.invalidateQueries({ queryKey: ['contact-full-data', contact.id] });
                  onRefreshConversation?.();
                }}
              />
            );
          })()}

          {/* Seção de Negócios (CRM) - movida para cima de Mídias */}
          {showDealTags && (
            <ContactDealsSection 
              contactId={contact.id}
              onRefresh={onRefreshConversation}
            />
          )}

          {/* Seção de Mídias e Documentos */}
          <ConversationMediaSection conversationId={conversation.id} />

          {/* v2.2.0: Separator removido — cards flutuantes em bg-muted/30 já delimitam as seções */}

          {/* Contact Details - Layout em 2 colunas — wrapper SectionCard (padrão modais) */}
          <SectionCard title="Detalhes">
            <div className="grid grid-cols-2 gap-2">
              {/* Categoria Kanban — seleção local (preview), persiste apenas ao escolher a fase */}
              <InlineCategoryPicker
                conversationId={conversation.id}
                contactId={contact.id}
                inboxId={conversation.inbox.id}
                currentPhase={mergedConversation.kanban_phase}
                pendingCategoryId={pendingCategoryId}
                onPendingCategoryChange={setPendingCategoryId}
                onChanged={onRefreshConversation}
              />

              {/* Fase Kanban — confirma a mudança (única gravação no banco) */}
              <InlinePhasePicker
                conversationId={conversation.id}
                contactId={contact.id}
                inboxId={conversation.inbox.id}
                currentPhase={mergedConversation.kanban_phase}
                pendingCategoryId={pendingCategoryId}
                onPendingCategoryChange={setPendingCategoryId}
                onChanged={onRefreshConversation}
              />

              {/* Email */}
              {contact.email && (
                <div className="flex items-start gap-2">
                  <div className="rounded-lg bg-muted p-1.5">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground">Email</p>
                    <p className="text-xs font-medium truncate">{contact.email}</p>
                  </div>
                </div>
              )}

              {/* Canal */}
              <div className="flex items-start gap-2">
                <div className="rounded-lg bg-muted p-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">Canal</p>
                  <p className="text-xs font-medium truncate">{conversation.inbox.name}</p>
                </div>
              </div>

              {/* Primeira conversa */}
              <div className="flex items-start gap-2">
                <div className="rounded-lg bg-muted p-1.5">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">Primeira conversa</p>
                  <p className="text-xs font-medium">
                    {format(new Date(conversation.created_at), "dd/MM/yyyy", {
                      locale: ptBR
                    })}
                  </p>
                </div>
              </div>

              {/* Salvo na agenda - dentro da grid de detalhes */}
              {contact.phone && contact.is_saved_to_agenda && (
                <div 
                  className="flex items-start gap-2 cursor-default"
                  title="Contato cadastrado no WhatsApp"
                >
                  <div className="rounded-lg bg-green-100 p-1.5">
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <div className="min-w-0 flex items-center gap-1">
                    <div>
                      <p className="text-[10px] text-green-600">Na agenda</p>
                      <p className="text-xs font-medium text-green-700">Salvo</p>
                    </div>
                    <Smartphone className="h-3.5 w-3.5 text-green-500" />
                  </div>
                </div>
              )}

              {/* Agente responsável - ocupa 2 colunas */}
              {conversation.assigned_agent && (
                <div className="flex items-start gap-2 col-span-2">
                  <div className="rounded-lg bg-muted p-1.5">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground">Agente responsável</p>
                    <p className="text-xs font-medium truncate">
                      {conversation.assigned_agent.full_name || 'Sem nome'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Botão para salvar na agenda - apenas quando NÃO está salvo
              NOTA: Meta WABA (meta-whatsapp) e Instagram não suportam - são APIs Cloud sem acesso à agenda do dispositivo */}
          {contact.phone && !contact.is_saved_to_agenda && 
            !['meta-whatsapp', 'meta-instagram'].includes(conversation.inbox?.provider_slug || '') && (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full" 
              onClick={handleAddToPhoneContacts} 
              disabled={isAddingContact}
            >
              {isAddingContact ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Salvar na agenda do WhatsApp
            </Button>
          )}

          {/* TEMPORARIAMENTE DESABILITADO - Aguardando correção integração WAHA
          {labelsSupported && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tags className="h-4 w-4 text-muted-foreground" />
                    <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Etiquetas
                    </h5>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6" 
                      onClick={() => setShowLabelManagerDialog(true)}
                      title="Gerenciar etiquetas"
                    >
                      <Settings className="h-3 w-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6" 
                      onClick={() => setShowLabelsDialog(true)}
                      title="Atribuir etiquetas"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {conversationLabels.length > 0 ? (
                  <LabelBadgeList 
                    labels={conversationLabels.map(cl => cl.label)} 
                    size="sm" 
                    maxVisible={5} 
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Nenhuma etiqueta atribuída
                  </p>
                )}
              </div>
            </>
          )}
          */}

          {/* Seção: Estatísticas - Movida para cima conforme solicitação */}
          {/* v2.2.0: Separator removido — cards flutuantes em bg-muted/30 já delimitam as seções */}
          <SectionCard title="Estatísticas">
            <div className={cn(
              "grid gap-3",
              sessionWindow.appliesSessionWindow ? "grid-cols-3" : "grid-cols-2"
            )}>
              {/* Card: Não lidas */}
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {conversation.unread_count}
                </p>
                <p className="text-xs text-muted-foreground">Não lidas</p>
              </div>
              
              {/* Card: Última msg */}
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {conversation.last_message_at ? format(new Date(conversation.last_message_at), 'HH:mm') : '—'}
                </p>
                <p className="text-xs text-muted-foreground">Última msg</p>
              </div>
              
              {/* Card: Janela 24h (WhatsApp Oficial e Instagram Direct) */}
              {sessionWindow.appliesSessionWindow && (
                <div className={cn(
                  "rounded-lg border p-3 text-center",
                  sessionWindow.isExpired 
                    ? "border-destructive/50 bg-destructive/5" 
                    : sessionWindow.remainingSeconds < 3600 
                      ? "border-amber-500/50 bg-amber-500/5"
                      : ""
                )}>
                  {sessionWindow.isExpired ? (
                    <>
                      <div className="flex items-center justify-center gap-1">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <p className="text-sm font-bold text-destructive">Expirada</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {sessionWindow.expiredSince}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-1">
                        <Clock className={cn(
                          "h-4 w-4",
                          sessionWindow.remainingSeconds < 3600 ? "text-amber-500" : "text-primary"
                        )} />
                        <p className={cn(
                          "text-lg font-bold font-mono",
                          sessionWindow.remainingSeconds < 3600 ? "text-amber-600" : "text-primary"
                        )}>
                          {sessionWindow.remainingTime}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">Janela 24h</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </SectionCard>


          {/* Seção: Tarefas vinculadas ao contato - exibida apenas se módulo de Tarefas estiver habilitado no tenant */}
          {(currentTenant?.tasks_enabled ?? false) && (
            <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
              <Collapsible open={tasksOpen} onOpenChange={setTasksOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Tarefas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(sidebarCounts?.tasks_count ?? 0) > 0 && (
                      <Badge variant="secondary" className="rounded-full text-xs">
                        {sidebarCounts?.tasks_count}
                      </Badge>
                    )}
                    <ChevronRight className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform duration-200",
                      tasksOpen && "rotate-90"
                    )} />
                  </div>
                </CollapsibleTrigger>
                {/* min-w-0 evita que filhos com min-width intrínseca (tabela 760px) empurrem o flex column do sidebar */}
                <CollapsibleContent className="px-1 pb-3 min-w-0">
                  {tasksOpen && (
                    // Container fixo: trava largura na área útil do sidebar e
                    // permite scroll horizontal interno das colunas da tabela
                    <div className="w-full max-w-[280px] min-w-0 overflow-hidden">
                      <TasksRelatedTabs context={{ contactId: contact.id }} compactHeader />
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Seção de Atividades Pendentes - visível para Admin/Supervisor/Vendedor */}
          {showDealTags && (
            <ContactActivitiesSection 
              contactId={contact.id}
              onRefresh={onRefreshConversation}
              initialCount={sidebarCounts?.activities_count}
            />
          )}


          {(showContactTags || showDealTags) && (
            <div className="space-y-3">
              {/* Card: Tags do Contato - visível para Admin/Supervisor/Agente */}
              {showContactTags && (
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <button
                    onClick={() => setShowTagsDialog(true)}
                    className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground">
                      Tags do Contato
                    </span>
                    <div className="flex items-center gap-2">
                    {(sidebarCounts?.contact_tags_count ?? contactTagsFromRpc.length ?? contact.tags.length) > 0 && (
                        <Badge variant="secondary" className="rounded-full text-xs">
                          {sidebarCounts?.contact_tags_count ?? contactTagsFromRpc.length ?? contact.tags.length}
                        </Badge>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                  
                  {/* Preview das tags — usa exclusivamente a RPC unificada como fonte de verdade.
                      Não usa fallback em contact.tags para evitar exibir tags removidas até F5. */}
                  {contactTagsFromRpc.length > 0 && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="flex flex-wrap gap-1.5">
                        {contactTagsFromRpc.slice(0, 4).map(tag => (
                          <DealTagBadge 
                            key={tag.id} 
                            name={tag.name} 
                            color={tag.color || '#6366f1'} 
                            size="sm"
                          />
                        ))}
                        {contactTagsFromRpc.length > 4 && (
                          <span className="text-xs text-muted-foreground self-center">
                            +{contactTagsFromRpc.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Card: Tags de Negócio (CRM) - visível para Admin/Supervisor/Vendedor */}
              {showDealTags && (
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between w-full px-4 py-4">
                    <span className="text-sm font-medium text-foreground">
                      Tags de Negócio
                    </span>
                    <div className="flex items-center gap-2">
                      {dealTagsData?.deals.length ? (
                        <Badge variant="secondary" className="rounded-full text-xs">
                          {dealTagsData.deals.length} {dealTagsData.deals.length === 1 ? 'negócio' : 'negócios'}
                        </Badge>
                      ) : null}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  
                  {/* Preview das tags de negócio */}
                  {loadingDealTags ? (
                    <div className="flex items-center gap-2 px-4 pb-4">
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Carregando...</span>
                    </div>
                  ) : dealTagsData?.tags.length ? (
                    <div className="px-4 pb-4">
                      <div className="flex flex-wrap gap-1.5">
                        {dealTagsData.tags.slice(0, 4).map(tag => (
                          <DealTagBadge 
                            key={tag.id} 
                            name={tag.name} 
                            color={tag.color} 
                            size="sm"
                          />
                        ))}
                        {dealTagsData.tags.length > 4 && (
                          <span className="text-xs text-muted-foreground self-center">
                            +{dealTagsData.tags.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 pb-4">
                      <p className="text-xs text-muted-foreground italic">
                        {dealTagsData?.deals.length ? 'Nenhuma tag nos negócios' : 'Nenhum negócio vinculado'}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Seção de Negócios movida para cima (linha ~552) */}
            </div>
          )}

          {/* Seção: Ações Rápidas (Regras Manuais) - Card Premium */}
          {currentTenant?.id && (
            <ManualRoutingSection
              inboxId={conversation.inbox_id}
              conversationId={conversation.id}
              contactId={contact.id}
              contactPhone={contact.phone || ''}
              contactName={contact.name}
              tenantId={currentTenant.id}
              initialCount={sidebarCounts?.quick_actions_count}
            />
          )}

          {/* Seção: Histórico de Conversas - Card Premium */}
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
            <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors">
                <span className="text-sm font-medium text-foreground">
                  Conversas Outras Inbox
                </span>
                <div className="flex items-center gap-2">
                  {/* Badge com contagem: usa dados carregados se disponível, senão usa contagem da RPC */}
                  {(conversationHistory ? conversationHistory.length : (sidebarCounts?.other_conversations_count ?? 0)) > 0 && (
                    <Badge variant="secondary" className="rounded-full text-xs">
                      {conversationHistory ? conversationHistory.length : sidebarCounts?.other_conversations_count}
                    </Badge>
                  )}
                  <ChevronRight className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    historyOpen && "rotate-90"
                  )} />
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="px-4 pb-4 space-y-2 overflow-hidden">
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : conversationHistory && conversationHistory.length > 0 ? (
                  <div className="space-y-2">
                    {conversationHistory.map((conv) => {
                      const inboxData = { name: conv.inbox_name, channel_type: conv.channel_type };
                      return (
                        <button
                          key={conv.id}
                          onClick={() => onNavigateToConversation?.(conv.id, conv.inbox_id)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-left overflow-hidden"
                        >
                          {/* Ícone baseado no tipo de canal da inbox */}
                          {(() => {
                            switch (inboxData?.channel_type) {
                              case 'whatsapp':
                              case 'whatsapp_oficial':
                                return (
                                  <div className="rounded-lg bg-green-100 p-2 shrink-0">
                                    <MessageSquare className="h-4 w-4 text-green-600" />
                                  </div>
                                );
                              case 'instagram':
                                return (
                                  <div className="rounded-lg bg-pink-100 p-2 shrink-0">
                                    <Instagram className="h-4 w-4 text-pink-500" />
                                  </div>
                                );
                              case 'messenger':
                                return (
                                  <div className="rounded-lg bg-blue-100 p-2 shrink-0">
                                    <MessagesSquare className="h-4 w-4 text-blue-500" />
                                  </div>
                                );
                              case 'telegram':
                                return (
                                  <div className="rounded-lg bg-sky-100 p-2 shrink-0">
                                    <Send className="h-4 w-4 text-sky-500" />
                                  </div>
                                );
                              case 'webchat':
                                return (
                                  <div className="rounded-lg bg-muted p-2 shrink-0">
                                    <Globe className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                );
                              default:
                                return (
                                  <div className="rounded-lg bg-muted p-2 shrink-0">
                                    <Inbox className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                );
                            }
                          })()}
                          <div className="flex-1 w-0 min-w-0 overflow-hidden">
                            {/* Linha 1: Título da inbox + Data */}
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-medium truncate">
                                {inboxData?.name || 'Inbox'}
                              </p>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {conv.last_message_at 
                                  ? format(new Date(conv.last_message_at), "dd/MM/yy", { locale: ptBR })
                                  : '—'}
                              </span>
                            </div>
                            {/* Linha 2: Preview da última mensagem + Badge de status */}
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <p className="text-xs text-muted-foreground truncate">
                                {conv.last_message_preview || 'Sem mensagens'}
                              </p>
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 shrink-0">
                                {conv.status}
                              </Badge>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    Nenhuma conversa em outras caixas de entrada
                  </p>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </ScrollArea>
      
      {/* TEMPORARIAMENTE DESABILITADO - Aguardando correção integração WAHA
      {labelsSupported && (
        <AssignLabelsDialog
          open={showLabelsDialog}
          onOpenChange={setShowLabelsDialog}
          availableLabels={availableLabels}
          currentLabelIds={conversationLabels.map(cl => cl.label_id)}
          isSaving={assigningLabels}
          conversationName={contact.name || undefined}
          onSave={async (labelIds) => {
            const chatId = `${contact.phone?.replace(/\D/g, '')}@c.us`;
            
            await assignLabels({
              conversationId: conversation.id,
              labelIds,
              chatId,
            });
          }}
        />
      )}
      
      {labelsSupported && (
        <Dialog open={showLabelManagerDialog} onOpenChange={setShowLabelManagerDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Gerenciar Etiquetas</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[calc(85vh-100px)]">
              <LabelManager 
                inboxId={conversation.inbox_id} 
                className="border-0 shadow-none"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
      */}
      
      {/* Dialog de atribuição de tags ao contato — usa lista da RPC unificada
          como fonte da seleção inicial, mantendo paridade com o atalho do header. */}
      <AssignTagsDialog
        open={showTagsDialog}
        onOpenChange={setShowTagsDialog}
        contact={{
          ...contact,
          // Fonte única da seleção inicial: RPC do sidebar, sem fallback stale.
          tags: contactTagsFromRpc.map(t => ({ id: t.id, name: t.name, color: t.color || '#6366f1' })),
        }}
        availableTags={availableTags}
        conversationId={conversation.id}
        tenantId={currentTenant?.id || ''}
        onSuccess={() => {
          // Atualiza dados da conversa no pai (mantém compatibilidade)
          if (onRefreshConversation) {
            onRefreshConversation();
          }
          // Lista lateral de conversas
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
          // RPC unificada do sidebar (contador + preview de tags) — mesma chave
          // usada pelo atalho do header em ChatArea.tsx
          queryClient.invalidateQueries({ queryKey: contactSidebarDetailsQueryKey });
          queryClient.refetchQueries({ queryKey: contactSidebarDetailsQueryKey, type: 'active' });
        }}
      />

      
      {/* Modal de Cliente Vinculado (CRM)
          Montagem condicional ao abrir: evita instanciar hooks filhos
          (e queries) enquanto o modal está fechado. */}
      {/* v2.0.0 da RPC: exigir id do cliente — linha sem vínculo não abre modal */}
      {linkedCustomer?.id && isCustomerModalOpen && (
        <CustomerDetailsModalPremium
          customerId={linkedCustomer.id}
          open={isCustomerModalOpen}
          onOpenChange={setIsCustomerModalOpen}
        />
      )}
    </div>;
}